import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import { colors } from '@commoncomponents/frontend-components/dist/styled';
import styled from 'styled-components';
import { ducks } from '../../..';
import { useDebounce } from '../../../hooks';
import { Wrapper } from '../styled';
import ActivationCodeInput from '../components/ActivationCodeInput';
import AccountsListView from '../components/AccountsListView';
import ModalNavigation from '../components/ModalNavigation';
import BottomButtons from '../components/BottomButtons';
import CodeInputBar from '../components/CodeInputBar';
import ModalHeader from '../components/ModalHeader';
import InfoBox from '../components/InfoBox';
import { CodeStatus } from '../constants';
import { getDurationText, formatDateToBeSeparatedByDots, formatPlanName, getPlanNameWithCapitalizedPlanType, isEntireCodeGiven, parseActivationCode, calculateDistance, } from '../utils';
import { getAccountsListViewMessages, getBottomButtonsMessages, getCodeInputBarMessages, getModalHeaderMessages, } from './messages';
const UpgradeWithCodeModal = ({ lang, organizationActiveUsers, organizationPaymentUsers, areActiveUsersLoading, translations, isMobileView, currentUserEmail, organizationCodeUser, codeDetails, planChangeInfo, plans, isProcessing, onClose, backToUpgradeWithPaymentModal, ORGANIZATION_ACTIVATION_CODES_ACTIONS: { CODE_DETAILS_REQUEST, ACTIVATE_CODE, SET_NEW_PLAN_INFO, SET_USER, CLEAR_DATA, STOP_PROCESSING, PLAN_CHANGE_INFO_REQUEST, }, PLAN_SUBSCRIPTIONS_ACTIONS: { GET_PLANS }, ORGANIZATION_ACTIVE_USERS_ACTIONS: { REQUEST }, }) => {
    const [searchText, setSearchText] = useState(null);
    const [codeErrorMessage, setCodeErrorMessage] = useState(null);
    const [newPlanInfoMessage, setNewPlanInfoMessage] = useState('');
    const [showOnlyOneUserAllowedWarning, setShowOnlyOneUserAllowedWarning] = useState(false);
    const [showCodeInputBar, setShowCodeInputBar] = useState(false);
    const [showPlanChangeWarning, setShowPlanChangeWarning] = useState(false);
    const disableMainButton = !organizationCodeUser || !newPlanInfoMessage;
    const showUserSelectionRequiredWarning = !organizationCodeUser &&
        codeDetails &&
        codeDetails[0].status === CodeStatus.OK;
    const handleSearchBarChange = (e) => {
        setSearchText(e.target.value);
    };
    const handleCodeActivation = () => ACTIVATE_CODE({
        userId: organizationCodeUser.boxExternalId,
        codeId: codeDetails[0].code,
    });
    const fetchPlanChangeinfo = () => PLAN_CHANGE_INFO_REQUEST({
        boxId: organizationCodeUser.boxExternalId,
        newPlanId: codeDetails[0].productId,
    });
    const onCodeInputChange = (code) => {
        const parsedCode = parseActivationCode(code);
        if (codeErrorMessage)
            setCodeErrorMessage(null);
        if (newPlanInfoMessage)
            setNewPlanInfoMessage('');
        if (isEntireCodeGiven(parsedCode)) {
            CODE_DETAILS_REQUEST(parsedCode);
        }
    };
    const getNewPlanInfo = () => {
        const newPlan = plans.find((plan) => plan.planId === codeDetails[0].productId);
        const expirationTime = {
            unit: newPlan.billingCycle.unit,
            count: newPlan.billingCycle.duration * newPlan.prices.PLN.billingCyclesCount,
        };
        return {
            name: getPlanNameWithCapitalizedPlanType(newPlan.translatedName),
            expirationTime: expirationTime,
            duration: getDurationText(expirationTime, lang),
        };
    };
    const setNewPlan = () => {
        const newPlan = getNewPlanInfo();
        SET_NEW_PLAN_INFO(newPlan);
        setNewPlanInfoMessage(`${newPlan.name} (${newPlan.duration})`);
    };
    const getPlanChangeWarning = () => {
        const currentPlanName = organizationCodeUser.planSubscription.name;
        const expirationDate = planChangeInfo.currentSubscriptionExpiration;
        const expirationDateText = `${formatDateToBeSeparatedByDots(expirationDate)} (${calculateDistance(expirationDate, lang)})`;
        const isCurrentUser = organizationCodeUser.boxIdentifier === currentUserEmail;
        return (React.createElement(FormattedMessage, Object.assign({}, (isCurrentUser
            ? translations.planChange.warning
            : translations.planChange.organizationUserWarning), { values: Object.assign({ b: (chunks) => React.createElement("b", null, chunks), planName: formatPlanName(currentPlanName), expirationDate: expirationDateText }, (!isCurrentUser && {
                username: `${organizationCodeUser.givenName} ${organizationCodeUser.surname}`,
            })) })));
    };
    const handleOnCancelClick = () => {
        CLEAR_DATA();
        backToUpgradeWithPaymentModal();
    };
    const handleOnCloseClick = () => {
        CLEAR_DATA();
        onClose();
    };
    const handleOnBackClick = () => {
        REQUEST();
        CLEAR_DATA();
        backToUpgradeWithPaymentModal();
    };
    useDebounce(() => {
        if (searchText !== null)
            REQUEST({ searchText });
    }, 350, [searchText]);
    useEffect(() => {
        if (codeDetails) {
            const status = codeDetails[0].status;
            switch (status) {
                case CodeStatus.OK:
                    return setNewPlan();
                case CodeStatus.NOT_FOUND:
                    return setCodeErrorMessage(translations.codeNotExistErrorText);
                case CodeStatus.EXPIRED:
                    return setCodeErrorMessage(translations.codeExpiredErrorText);
                case CodeStatus.USED:
                    return setCodeErrorMessage(translations.codeAlreadyUsedErrorText);
            }
        }
    }, [codeDetails]);
    useEffect(() => {
        GET_PLANS();
    }, [lang]);
    useEffect(() => {
        if (!isMobileView) {
            if (organizationPaymentUsers.length === 1)
                SET_USER(organizationPaymentUsers[0]);
            else if (organizationPaymentUsers.length !== 0)
                setShowOnlyOneUserAllowedWarning(true);
        }
    }, [organizationPaymentUsers]);
    useEffect(() => {
        if (showPlanChangeWarning) {
            setShowPlanChangeWarning(false);
        }
        if ((showOnlyOneUserAllowedWarning && organizationCodeUser) ||
            showUserSelectionRequiredWarning)
            setShowOnlyOneUserAllowedWarning(false);
    }, [organizationCodeUser, showUserSelectionRequiredWarning]);
    useEffect(() => {
        if (planChangeInfo) {
            if (planChangeInfo.warnUserAboutCurrentSubscription === true ||
                planChangeInfo.unprofitableChange === true) {
                STOP_PROCESSING();
                setShowPlanChangeWarning(true);
            }
            else {
                handleCodeActivation();
            }
        }
    }, [planChangeInfo]);
    return (React.createElement(Wrapper, { className: "modal-plans" },
        React.createElement(ModalNavigation, { onModalClose: handleOnCloseClick, shouldBeBackButton: true, onBack: handleOnBackClick }),
        React.createElement(ModalHeader, { translations: getModalHeaderMessages(translations), handleSearchBarChange: handleSearchBarChange, isUpgradeWithPaymentView: false, shouldDisplayWarning: showOnlyOneUserAllowedWarning && !isMobileView }),
        React.createElement(AccountsListView, { translations: getAccountsListViewMessages(translations), accountsList: organizationActiveUsers, currentUserEmail: currentUserEmail, isUpgradeWithPaymentView: false, isMobileView: isMobileView, areAccountsLoading: areActiveUsersLoading, setShowCodeInputBar: setShowCodeInputBar }),
        !isMobileView && (React.createElement(BottomButtons, { translations: getBottomButtonsMessages(translations), onCancelButtonClick: handleOnCancelClick, onMainButtonClick: planChangeInfo ? handleCodeActivation : fetchPlanChangeinfo, shouldMainButtonBeDisabled: disableMainButton, shouldShowCodeInput: true, isMobileView: isMobileView, onCodeInputChange: onCodeInputChange, codeErrorMessage: codeErrorMessage, newPlanInfoMessage: newPlanInfoMessage, isProcessing: isProcessing, showUserSelectionRequiredWarning: showUserSelectionRequiredWarning, planChangeWarning: planChangeInfo && showPlanChangeWarning && getPlanChangeWarning() })),
        !isMobileView && (React.createElement(InfoBox, { header: translations.consumerRightsHeader, text: translations.consumerRightsText })),
        React.createElement(CodeInputBar, { open: showCodeInputBar, translations: getCodeInputBarMessages(translations), codeErrorMessage: codeErrorMessage, newPlanInfoMessage: newPlanInfoMessage, buttonDisabled: disableMainButton, onCodeInputChange: onCodeInputChange, isProcessing: isProcessing, onClose: () => setShowCodeInputBar((prev) => !prev), onActivateClick: planChangeInfo ? handleCodeActivation : fetchPlanChangeinfo, planChangeWarning: planChangeInfo && showPlanChangeWarning && getPlanChangeWarning() }),
        isMobileView && organizationCodeUser && (React.createElement(CodeInputWrapper, null,
            React.createElement(ActivationCodeInput, { label: translations.enterCodeInputLabel, onFocus: () => {
                    setShowCodeInputBar(true);
                }, disabled: showCodeInputBar, errorMessage: codeErrorMessage, infoMessage: newPlanInfoMessage }),
            React.createElement(InfoBox, { header: translations.consumerRightsHeader, text: translations.consumerRightsText })))));
};
const CodeInputWrapper = styled.div `
  background-color: ${colors.snowy};
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 32px rgba(41, 51, 61, 0.08);
  padding: 16px;
  min-height: 162px;
  z-index: 2;
  width: 100%;

  .info-box {
    padding: 12px 0 0;
  }
`;
export const mapStateToProps = (state) => ({
    lang: state.lang.get('lang'),
    organizationActiveUsers: state.organizationActiveUsers.get('data'),
    organizationPaymentUsers: state.organizationPayment.get('selectedUsers'),
    areActiveUsersLoading: state.organizationActiveUsers.get('isProcessing'),
    organizationCodeUser: state.organizationActivationCodes.get('user'),
    codeDetails: state.organizationActivationCodes.get('codeDetails'),
    planChangeInfo: state.organizationActivationCodes.get('planChangeInfo'),
    isProcessing: state.organizationActivationCodes.get('isProcessing'),
    plans: state.planSubscriptions.get('planSubscriptions'),
});
export const mapDispatchToProps = (dispatch) => ({
    ORGANIZATION_ACTIVATION_CODES_ACTIONS: bindActionCreators(ducks.ORGANIZATION_ACTIVATION_CODES_ACTIONS, dispatch),
    PLAN_SUBSCRIPTIONS_ACTIONS: bindActionCreators(ducks.PLAN_SUBSCRIPTIONS_ACTIONS, dispatch),
    ORGANIZATION_ACTIVE_USERS_ACTIONS: bindActionCreators(ducks.ORGANIZATION_ACTIVE_USERS_ACTIONS, dispatch),
});
export default connect(mapStateToProps, mapDispatchToProps)(UpgradeWithCodeModal);
